// ./src/store/slices/userSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { PersistPartial } from 'redux-persist/es/persistReducer';

interface NotificationPreferences {
    email: boolean;
    sms: boolean;
}

interface DismissedMessage {
    messageId: string;
    dismissedAt: string;
    expiresAt?: string;
}

export interface UserData {
    userId: number;
    firstName: string;
    lastName: string;
    username?: string;
    email: string;
    roles: string[];
    createdAt: string;
    updatedAt: string;
    lastLogin: string;
    phone?: string;
    password?: string;
    firstProjectId?: number; // 🎯 NEW: ID of user's first sent project
    freeProjectUsed?: boolean; // 🎯 NEW: Whether user has used their free project
    notifications: {
        invitationOpened: NotificationPreferences;
        invitationReply: NotificationPreferences;
        emailNotDelivered: NotificationPreferences;
        automatedMessages: NotificationPreferences;
    };
    newsletterConsent?: boolean;
    favoriteDesignIds: number[];
    dismissedMessages: Record<string, DismissedMessage>;
}

interface UserState {
    isAuthenticated: boolean;
    isEmailConfirmed: boolean;
    confirmationError: string | null;
    isConfirmingEmail: boolean;
    userData: UserData | null;
    token: string | null;
    isAdmin: boolean;
    loading: boolean;
    registerError: string | null;
    isRegistering: boolean;
    sessionData: {
        sessionStart: number | null;
        lastActivity: number | null;
    };
}

const initialState: UserState = {
    isAuthenticated: false,
    isEmailConfirmed: false,
    confirmationError: null,
    isConfirmingEmail: false,
    userData: {
        userId: 0,
        firstName: '',
        lastName: '',
        username: '',
        email: '',
        roles: [],
        createdAt: '',
        updatedAt: '',
        lastLogin: '',
        phone: '',
        notifications: {
            invitationOpened: { email: false, sms: false },
            invitationReply: { email: false, sms: false },
            emailNotDelivered: { email: false, sms: false },
            automatedMessages: { email: false, sms: false },
        },
        newsletterConsent: false,
        favoriteDesignIds: [],
        dismissedMessages: {},
    },
    token: null,
    isAdmin: false,
    loading: false,
    registerError: null,
    isRegistering: false,
    sessionData: {
        sessionStart: null,
        lastActivity: null,
    },
};

export type UserStateWithPersist = UserState & PersistPartial;

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        // Set the user as authenticated or not
        setUserAuthenticated(state, action: PayloadAction<boolean>) {
            state.isAuthenticated = action.payload;
        },
        // Set user data
        setUserData(state, action: PayloadAction<Omit<UserData, 'token'> | null>) {
            state.userData = action.payload;
            // Ensure dismissedMessages is always initialized
            if (state.userData && !state.userData.dismissedMessages) {
                state.userData.dismissedMessages = {};
            }
        },
        // Set authentication token
        setToken(state, action: PayloadAction<string | null>) {
            state.token = action.payload;
        },
        // Set admin status
        setIsAdmin(state, action: PayloadAction<boolean>) {
            state.isAdmin = action.payload;
        },
        // Set loading state for user data
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        },
        // Toggle authentication state
        toggleAuth(state) {
            state.isAuthenticated = !state.isAuthenticated;
        },
        // Email confirmation process
        confirmEmailRequest(state) {
            state.isConfirmingEmail = true;
            state.confirmationError = null;
        },
        confirmEmailSuccess(state, action: PayloadAction<{ token: string; userData: UserData }>) {
            state.isConfirmingEmail = false;
            state.isEmailConfirmed = true;
            state.isAuthenticated = true;
            state.token = action.payload.token;
            state.userData = action.payload.userData; // Update user data
        },
        confirmEmailFailure(state, action: PayloadAction<string>) {
            state.isConfirmingEmail = false;
            state.confirmationError = action.payload;
        },
        // Registration process
        registerNewUserRequest(state) {
            state.isRegistering = true;
            state.registerError = null;
        },
        registerNewUserSuccess(state) {
            state.isRegistering = false;
        },
        registerNewUserFailure(state, action: PayloadAction<string>) {
            state.isRegistering = false;
            state.registerError = action.payload;
        },
        updateUserInfo(state, action: PayloadAction<Partial<UserData>>) {
            if (state.userData) {
                state.userData = {
                    ...state.userData,
                    ...action.payload, // Merge the updated fields with the existing user data
                };
            }
        },
        updateFavorites(state, action: PayloadAction<number[]>) {
            // Overwrite the array of favoriteDesignIds
            if (state.userData) {
                state.userData.favoriteDesignIds = action.payload;
            }
        },
        // Message dismissal actions
        dismissMessage: (state, action: PayloadAction<{
            messageId: string;
            expiresAt?: string;
        }>) => {
            if (state.userData) {
                // Initialize dismissedMessages if it doesn't exist
                if (!state.userData.dismissedMessages) {
                    state.userData.dismissedMessages = {};
                }
                
                state.userData.dismissedMessages[action.payload.messageId] = {
                    messageId: action.payload.messageId,
                    dismissedAt: new Date().toISOString(),
                    expiresAt: action.payload.expiresAt
                };
            }
        },
        resetMessage: (state, action: PayloadAction<string>) => {
            if (state.userData && state.userData.dismissedMessages) {
                delete state.userData.dismissedMessages[action.payload];
            }
        },
        setDismissedMessages: (state, action: PayloadAction<Record<string, DismissedMessage>>) => {
            if (state.userData) {
                state.userData.dismissedMessages = action.payload;
            }
        },
        // Session data actions
        setSessionData(state, action: PayloadAction<{ sessionStart: number; lastActivity: number }>) {
            if (!state.sessionData) {
                state.sessionData = { sessionStart: null, lastActivity: null };
            }
            state.sessionData.sessionStart = action.payload.sessionStart;
            state.sessionData.lastActivity = action.payload.lastActivity;
        },
        updateLastActivity(state, action: PayloadAction<number>) {
            if (!state.sessionData) {
                state.sessionData = { sessionStart: null, lastActivity: null };
            }
            state.sessionData.lastActivity = action.payload;
        },
        clearSessionData(state) {
            if (!state.sessionData) {
                state.sessionData = { sessionStart: null, lastActivity: null };
            }
            state.sessionData.sessionStart = null;
            state.sessionData.lastActivity = null;
        },
    },
});

// Export the actions
export const {
    setUserAuthenticated,
    setUserData,
    setToken,
    setIsAdmin,
    setLoading,
    toggleAuth,
    confirmEmailRequest,
    confirmEmailSuccess,
    confirmEmailFailure,
    registerNewUserRequest,
    registerNewUserSuccess,
    registerNewUserFailure,
    updateUserInfo,
    updateFavorites,
    dismissMessage,
    resetMessage,
    setDismissedMessages,
    setSessionData,
    updateLastActivity,
    clearSessionData
} = userSlice.actions;

// Export the reducer
export default userSlice.reducer;

// Export types
export type { DismissedMessage };

// Selector to check if authentication is ready
export const selectAuthReady = (state: RootState): boolean => {
    return !!(state.user.token && state.user.isAuthenticated);
};